/**
 * Pure helpers behind the portfolio view (JARVIS-126): the per-category
 * arrangement, category limits, the priority lane, the tag filter and the
 * view sorts/filters. No React, no Firestore — everything here is unit-tested
 * in `portfolioArrangement.test.ts`.
 *
 * Model decisions (see .cursor/skills/portfolios/SKILL.md):
 *   - Priority = `priorityLaneProjectIds`, an ordered array. Membership and
 *     order in one field. `planningPriorityRank` is retired.
 *   - Order = category, then `categorySortIndex` within the category. Indexes
 *     are per category; a move between categories renumbers only the two
 *     groups involved. `projectOrder` is always rewritten as the flattened
 *     arrangement so single-list readers stay correct.
 *   - Limits (`PORTFOLIO_CATEGORY_LIMITS`) are enforced here, in the move/add
 *     path, never in the renderer.
 */
import dayjs from "dayjs"
import { sortBy } from "lodash"

import {
  PORTFOLIO_CATEGORY,
  PORTFOLIO_CATEGORY_LIMITS,
  type PortfolioCategory,
} from "#customTypes/composedTypes/portfolioTypes"

import type {
  PortfolioMockProject,
  PortfolioMockStage,
} from "./portfolioUiTypes"

export type ProjectPlacement = "before" | "after"

/** Display order of the categories: the two submission groups first. */
export const PORTFOLIO_CATEGORY_ORDER: readonly PortfolioCategory[] = [
  PORTFOLIO_CATEGORY.SELECTED,
  PORTFOLIO_CATEGORY.SUSTAINED,
  PORTFOLIO_CATEGORY.PAUSED,
  PORTFOLIO_CATEGORY.UNASSIGNED,
]

const categoryRank = (category: PortfolioCategory): number => {
  const rank = PORTFOLIO_CATEGORY_ORDER.indexOf(category)
  return rank < 0 ? PORTFOLIO_CATEGORY_ORDER.length : rank
}

const UNINDEXED = Number.MAX_SAFE_INTEGER

/**
 * "Teacher's arrangement": category order, then `categorySortIndex`. Stable,
 * so projects without an index keep their incoming (`projectOrder`) position
 * after the indexed ones in their category.
 */
export const sortProjectsByArrangement = <T extends PortfolioMockProject>(
  projects: readonly T[]
): T[] =>
  sortBy(
    projects,
    (project) => categoryRank(project.category),
    (project) => project.categorySortIndex ?? UNINDEXED
  )

/**
 * Assigns dense 0..n-1 `categorySortIndex` values per category, in the order
 * given. Callers sort first (or hand in an intentionally arranged list).
 */
export const renumberCategorySortIndexes = <T extends PortfolioMockProject>(
  projects: readonly T[]
): T[] => {
  const nextIndexByCategory: Partial<Record<PortfolioCategory, number>> = {}
  return projects.map((project) => {
    const next = nextIndexByCategory[project.category] ?? 0
    nextIndexByCategory[project.category] = next + 1
    return project.categorySortIndex === next
      ? project
      : { ...project, categorySortIndex: next }
  })
}

/** Sort, then renumber — the canonical arrangement to persist. */
export const normalizeArrangement = <T extends PortfolioMockProject>(
  projects: readonly T[]
): T[] => renumberCategorySortIndexes(sortProjectsByArrangement(projects))

// --- Category limits -----------------------------------------------------------

export type ArrangementError =
  | {
      code: "category_limit_reached"
      category: PortfolioCategory
      limit: number
    }
  | { code: "unassigned_is_fixed" }
  | { code: "not_found" }
  | { code: "empty_title" }

export type ArrangementResult<T> =
  | { ok: true; projects: T[] }
  | { ok: false; error: ArrangementError }

export const countProjectsInCategory = (
  projects: readonly PortfolioMockProject[],
  category: PortfolioCategory,
  excludingProjectId?: string
): number =>
  projects.filter(
    (project) =>
      project.category === category && project.id !== excludingProjectId
  ).length

/**
 * True when one more project may enter `category`. `excludingProjectId` is the
 * project being moved, so a same-category reorder never counts itself.
 */
export const canPlaceInCategory = (
  projects: readonly PortfolioMockProject[],
  category: PortfolioCategory,
  excludingProjectId?: string
): boolean =>
  countProjectsInCategory(projects, category, excludingProjectId) <
  PORTFOLIO_CATEGORY_LIMITS[category]

const limitError = (category: PortfolioCategory): ArrangementError => ({
  code: "category_limit_reached",
  category,
  limit: PORTFOLIO_CATEGORY_LIMITS[category],
})

/**
 * Drops `sourceId` before/after `targetId`, adopting the target's category,
 * and renumbers. Refuses to move into a full category, and never moves the
 * scanner-owned Unassigned project (or anything into Unassigned).
 */
export const reorderProjectInArrangement = <T extends PortfolioMockProject>(
  projects: readonly T[],
  sourceId: string,
  targetId: string,
  placement: ProjectPlacement = "before"
): ArrangementResult<T> => {
  const sourceIndex = projects.findIndex((project) => project.id === sourceId)
  const targetIndex = projects.findIndex((project) => project.id === targetId)
  if (sourceIndex < 0 || targetIndex < 0) {
    return { ok: false, error: { code: "not_found" } }
  }
  if (sourceIndex === targetIndex) return { ok: true, projects: [...projects] }

  const source = projects[sourceIndex]
  const target = projects[targetIndex]
  const changesCategory = source.category !== target.category
  if (
    source.category === PORTFOLIO_CATEGORY.UNASSIGNED ||
    (changesCategory && target.category === PORTFOLIO_CATEGORY.UNASSIGNED)
  ) {
    return { ok: false, error: { code: "unassigned_is_fixed" } }
  }
  if (
    changesCategory &&
    !canPlaceInCategory(projects, target.category, sourceId)
  ) {
    return { ok: false, error: limitError(target.category) }
  }

  const next = [...projects]
  const [moved] = next.splice(sourceIndex, 1)
  const adjustedTargetIndex =
    sourceIndex < targetIndex ? targetIndex - 1 : targetIndex
  const destinationIndex =
    placement === "before" ? adjustedTargetIndex : adjustedTargetIndex + 1
  next.splice(
    Math.max(0, Math.min(next.length, destinationIndex)),
    0,
    changesCategory ? { ...moved, category: target.category } : moved
  )
  // Renumber in the new list order without re-sorting, so a drop that lands
  // a project between two of its new siblings keeps exactly that slot.
  return { ok: true, projects: renumberCategorySortIndexes(next) }
}

/**
 * Moves a project to the end of another category (a drop on the category
 * chip, or the category select). Limit-checked; Unassigned is fixed.
 */
export const moveProjectToCategoryInArrangement = <
  T extends PortfolioMockProject,
>(
  projects: readonly T[],
  projectId: string,
  category: PortfolioCategory
): ArrangementResult<T> => {
  const project = projects.find((candidate) => candidate.id === projectId)
  if (!project) return { ok: false, error: { code: "not_found" } }
  if (project.category === category)
    return { ok: true, projects: [...projects] }
  if (
    project.category === PORTFOLIO_CATEGORY.UNASSIGNED ||
    category === PORTFOLIO_CATEGORY.UNASSIGNED
  ) {
    return { ok: false, error: { code: "unassigned_is_fixed" } }
  }
  if (!canPlaceInCategory(projects, category, projectId)) {
    return { ok: false, error: limitError(category) }
  }
  const lastIndexInTarget = projects.reduce(
    (max, candidate) =>
      candidate.category === category
        ? Math.max(max, candidate.categorySortIndex ?? 0)
        : max,
    -1
  )
  const moved = {
    ...project,
    category,
    categorySortIndex: lastIndexInTarget + 1,
  }
  return {
    ok: true,
    projects: normalizeArrangement(
      projects.map((candidate) =>
        candidate.id === projectId ? moved : candidate
      )
    ),
  }
}

/**
 * Inserts a new project at the FRONT of its category (the previous grid put
 * new projects first, and the newest-first convention is what teachers know).
 */
export const insertNewProjectInArrangement = <T extends PortfolioMockProject>(
  projects: readonly T[],
  project: T
): ArrangementResult<T> => {
  if (!canPlaceInCategory(projects, project.category)) {
    return { ok: false, error: limitError(project.category) }
  }
  const withNew = [{ ...project, categorySortIndex: -1 }, ...projects]
  return { ok: true, projects: normalizeArrangement(withNew) }
}

export const describeArrangementError = (
  error: ArrangementError,
  labels: Record<PortfolioCategory, string>
): string => {
  switch (error.code) {
    case "category_limit_reached":
      return `${labels[error.category]} is full (${error.limit} max). Move or pause a project first.`
    case "unassigned_is_fixed":
      return "Unassigned artwork is managed by the scanner and can't be moved."
    case "not_found":
      return "That project no longer exists."
    case "empty_title":
      return "A project needs a title."
  }
}

// --- Priority lane ---------------------------------------------------------------

export const PORTFOLIO_PRIORITY_LANE_MAX = 10

export const clampPriorityLane = (
  ids: readonly string[],
  max: number = PORTFOLIO_PRIORITY_LANE_MAX
): string[] => Array.from(new Set(ids)).slice(0, max)

/** 1-based lane position for the `#N` badge, or undefined when not in the lane. */
export const getPriorityPosition = (
  lane: readonly string[],
  projectId: string
): number | undefined => {
  const index = lane.indexOf(projectId)
  return index < 0 ? undefined : index + 1
}

/** Adds to the END of the lane (new priorities queue up behind existing ones). */
export const addToPriorityLane = (
  lane: readonly string[],
  projectId: string,
  max: number = PORTFOLIO_PRIORITY_LANE_MAX
): string[] =>
  lane.includes(projectId)
    ? [...lane]
    : clampPriorityLane([...lane, projectId], max)

export const removeFromPriorityLane = (
  lane: readonly string[],
  projectId: string
): string[] => lane.filter((id) => id !== projectId)

/**
 * The lane's drag-drop reorder: `sourceId` lands before/after `targetId`.
 * Both must already be in the lane — membership changes go through add/remove.
 */
export const reorderPriorityLane = (
  lane: readonly string[],
  sourceId: string,
  targetId: string,
  placement: ProjectPlacement = "before"
): string[] => {
  const sourceIndex = lane.indexOf(sourceId)
  const targetIndex = lane.indexOf(targetId)
  if (sourceIndex < 0 || targetIndex < 0 || sourceIndex === targetIndex) {
    return [...lane]
  }
  const next = [...lane]
  const [moved] = next.splice(sourceIndex, 1)
  const adjustedTargetIndex =
    sourceIndex < targetIndex ? targetIndex - 1 : targetIndex
  const destinationIndex =
    placement === "before" ? adjustedTargetIndex : adjustedTargetIndex + 1
  next.splice(Math.max(0, Math.min(next.length, destinationIndex)), 0, moved)
  return next
}

/**
 * The hover ⚡ controls: promote = one place earlier, demote = one place
 * later. Expressed as the (target, placement) pair `reorderPriorityLane`
 * takes, so the UI can hand it straight to `reorderProjectPriority`. Null at
 * the ends of the lane.
 */
export const getPriorityStep = (
  lane: readonly string[],
  projectId: string,
  direction: "promote" | "demote"
): { targetId: string; placement: ProjectPlacement } | null => {
  const index = lane.indexOf(projectId)
  if (index < 0) return null
  if (direction === "promote") {
    return index === 0
      ? null
      : { targetId: lane[index - 1], placement: "before" }
  }
  return index >= lane.length - 1
    ? null
    : { targetId: lane[index + 1], placement: "after" }
}

// --- Tags -----------------------------------------------------------------------------

/** The toolbar's tag filter: AND across the chosen tag ids. */
export const projectHasEveryTagId = (
  project: Pick<PortfolioMockProject, "tagIds">,
  requiredTagIds: readonly string[]
): boolean => {
  if (!requiredTagIds.length) return true
  const owned = new Set(project.tagIds ?? [])
  return requiredTagIds.every((tagId) => owned.has(tagId))
}

// --- View sorts + lane filter ------------------------------------------------------

export const PORTFOLIO_VIEW_SORT = {
  RECENT: "recent",
  ARRANGEMENT: "arrangement",
  PRIORITY: "priority",
} as const

export type PortfolioViewSort =
  (typeof PORTFOLIO_VIEW_SORT)[keyof typeof PORTFOLIO_VIEW_SORT]

export const PORTFOLIO_VIEW_SORT_OPTIONS: ReadonlyArray<{
  value: PortfolioViewSort
  label: string
}> = [
  { value: PORTFOLIO_VIEW_SORT.ARRANGEMENT, label: "Teacher's arrangement" },
  { value: PORTFOLIO_VIEW_SORT.RECENT, label: "Most recently edited" },
  { value: PORTFOLIO_VIEW_SORT.PRIORITY, label: "Priority first" },
]

const toMs = (value: string | undefined): number => {
  if (!value) return 0
  const ms = dayjs(value).valueOf()
  return Number.isFinite(ms) ? ms : 0
}

/** Latest activity anywhere on the project: its own stamp or any step's. */
export const getProjectLastEditedMs = (project: PortfolioMockProject): number =>
  Math.max(
    toMs(project.lastActivityAt),
    toMs(project.createdAt),
    ...project.stages.map((stage) =>
      Math.max(toMs(stage.lastActivityAt), toMs(stage.createdAt))
    )
  )

/** The tile cover: the last step (in step order) that has an image, else the project cover. */
export const getProjectCoverStage = (
  project: PortfolioMockProject
): PortfolioMockStage | undefined =>
  [...project.stages].reverse().find((stage) => stage.imageUrl?.trim())

export const getProjectCoverUrl = (project: PortfolioMockProject): string =>
  getProjectCoverStage(project)?.imageUrl || project.coverImageUrl || ""

export const sortProjectsForView = <T extends PortfolioMockProject>(
  projects: readonly T[],
  sort: PortfolioViewSort,
  lane: readonly string[]
): T[] => {
  const arranged = sortProjectsByArrangement(projects)
  switch (sort) {
    case PORTFOLIO_VIEW_SORT.RECENT:
      return sortBy(arranged, (project) => -getProjectLastEditedMs(project))
    case PORTFOLIO_VIEW_SORT.PRIORITY:
      return sortBy(arranged, (project) => {
        const position = lane.indexOf(project.id)
        return position < 0 ? UNINDEXED : position
      })
    case PORTFOLIO_VIEW_SORT.ARRANGEMENT:
      return arranged
  }
}

export const PRIORITY_LANE_CHIP = "priority" as const

export type PortfolioLaneChip = PortfolioCategory | typeof PRIORITY_LANE_CHIP

/**
 * Lane chips are a union filter: no chip checked shows everything; otherwise a
 * project shows when its category is checked, or the ⚡ chip is checked and
 * it is in the lane.
 */
export const filterProjectsByLaneChips = <T extends PortfolioMockProject>(
  projects: readonly T[],
  checked: ReadonlySet<PortfolioLaneChip>,
  lane: readonly string[]
): T[] => {
  if (checked.size === 0) return [...projects]
  const laneSet = new Set(lane)
  return projects.filter(
    (project) =>
      checked.has(project.category) ||
      (checked.has(PRIORITY_LANE_CHIP) && laneSet.has(project.id))
  )
}
